import React, { Component } from "react";
import PropTypes from "prop-types";
import { Modal, Form } from "semantic-ui-react";
import { observer } from "mobx-react";
import cx from "classnames";
import PromiseButton from "../../components/element/PromiseButton";
import Tooltip from "../../components/autowire/Tooltip";
import TourDeliver from "../../stores/TourDeliver";
import StripeTease from "./StripeTease";
import TourProduct from "./TourProduct";
import CreditCardsInput from "./CreditCardsInput";
import CurrenciesInput from "./CurrenciesInput";
import AmountDueInput from "./AmountDueInput";
import DescriptionInput from "./DescriptionInput";
import HandoffNotes from "./HandoffNotesInput";
import InvoiceInput from "./InvoiceInput";
import Sidebar from "./Sidebar";

@observer
class Ransom extends Component {
  getHandoffNotes() {
    const { store } = this.props;
    return (
      <HandoffNotes
        handoffNotes={store.handoffNotes}
        setHandoffNotesCallback={handoffNotes => store.setHandoffNotes(handoffNotes)}
      />
    );
  }

  getProducts() {
    const { store } = this.props;

    return (
      <section className="ransom-section">
        <div className="section-content">
          {store.tourProducts.map(tourProduct => (
            <TourProduct
              key={tourProduct.productId}
              tourProduct={tourProduct}
              changeCallback={() => store.setSelectedTourProduct(tourProduct.productId)}
            />
          ))}
        </div>
      </section>
    );
  }

  getSubHeader() {
    const { store } = this.props;

    if (!store.canPublishTour) {
      return null;
    }

    if (store.action === "RANSOM") {
      return (
        <div className="sub-header">
          <strong>Delivery Options</strong>
          &nbsp;&gt; Awaiting Payment Notification &gt; Tour Announcement
        </div>
      );
    }

    return (
      <div className="sub-header">
        <strong>Delivery Options</strong>
        &nbsp;&gt; Email Notification
      </div>
    );
  }

  getPaymentCollectionSection() {
    const { store } = this.props;

    if (!store.isPaymentCollectionVisible) {
      return null;
    }

    // Configure common widgets that show/hide when the users choses an action (publish, invoice, ransom, charge card).
    let invoiceInput = null;

    if (store.hasInvoicingSystem) {
      invoiceInput = (
        <InvoiceInput
          invoicingSystemTitle={store.invoicingSystemTitle}
          invoiceId={store.invoiceId}
          setInvoiceIdCallback={value => store.setInvoiceId(value)}
        />
      );
    }

    let creditCardsInput = null;

    if (store.customerHasCreditCards) {
      creditCardsInput = (
        <CreditCardsInput
          creditCard={store.creditCard}
          creditCards={store.creditCards}
          setCreditCardCallback={value => store.setCreditCard(value)}
        />
      );
    }

    const currenciesInput = (
      <CurrenciesInput
        currency={store.currency}
        currencies={store.currencies}
        setCurrencyCallback={value => store.setCurrency(value)}
      />
    );

    const amountDueError = store.ransomErrors ? store.ransomErrors.amountDue : "";
    const amountDueInput = (
      <AmountDueInput
        value={store.amountDue}
        error={amountDueError}
        setAmountDueCallback={value => store.setAmountDue(value)}
      />
    );

    const descriptionInput = (
      <DescriptionInput description={store.description} setDescriptionCallback={value => store.setDescription(value)} />
    );

    const stripeIsDownMessage = (
      <div className="ui red message">
        Charging Card on File is disabled because the payment gateway services are temporarily
        <a target="_blank" rel="noopener noreferrer" href="https://status.stripe.com/">
          unavailable.
        </a>
      </div>
    );

    return (
      <section className="ransom-section">
        <p>
          <strong>Payment Collection</strong>
        </p>
        {store.shouldDisplayStripeIsDownWarning ? stripeIsDownMessage : null}
        <div className="section-content">
          {store.isStripeTeaseVisible ? (
            <StripeTease stripeAuthUrl={store.stripeAuthUrl} providerId={store.providerId} />
          ) : null}
          <div>
            <div id="ransomOrChargeContainer">
              <div>
                <input
                  onChange={event => store.setAction(event.target.value)}
                  id="publishInput"
                  name="ransomOrCharge"
                  value="PUBLISH"
                  type="radio"
                  checked={store.action === "PUBLISH"}
                />
                <label className="ransom-option" htmlFor="publishInput">
                  No payment required
                </label>
              </div>
              <div>
                <input
                  onChange={event => store.setAction(event.target.value)}
                  id="publishWithInvoiceInput"
                  name="ransomOrCharge"
                  value="PUBLISH_WITH_INVOICE"
                  type="radio"
                  checked={store.action === "PUBLISH_WITH_INVOICE"}
                  disabled={!store.hasInvoicingSystem}
                />
                <label
                  className={cx({
                    "ransom-option": true,
                    disabled: !store.hasInvoicingSystem,
                  })}
                  htmlFor="publishWithInvoiceInput"
                >
                  Invoice Only
                </label>
                &nbsp;
                {!store.hasInvoicingSystem ? (
                  <Tooltip
                    size="small"
                    wide="very"
                    message={`If you’d like to add an invoicing number to your customers tours,
                        head to My Account > Customer Invoices to enable this option`}
                  />
                ) : null}
                {store.action === "PUBLISH_WITH_INVOICE" && store.hasInvoicingSystem ? (
                  <div className="section-content">
                    <div>{invoiceInput}</div>
                  </div>
                ) : null}
              </div>
              <div>
                <input
                  onChange={event => store.setAction(event.target.value)}
                  id="ransomInput"
                  name="ransomOrCharge"
                  value="RANSOM"
                  type="radio"
                  checked={store.action === "RANSOM"}
                  disabled={store.ransomDisabled}
                />
                <label
                  className={cx({
                    "ransom-option": true,
                    disabled: store.ransomDisabled,
                  })}
                  htmlFor="ransomInput"
                >
                  Require payment to release tour
                </label>
                &nbsp;
                {store.ransomDisabled ? (
                  <Tooltip
                    size="small"
                    wide
                    message={`This option can only be leveraged on unpublished tours, as your customers
                        already have access to their media`}
                  />
                ) : null}
                {store.ransomDisabled ? null : (
                  <a
                    className="what-customer-see"
                    href="https://support.tourbuzz.net/hc/en-us/articles/115003834326-Client-Panel-How-to-Access-a-Tour-Awaiting-Payment"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Create great customer experiences with this option
                  </a>
                )}
                {store.action === "RANSOM" && store.hasStripe ? (
                  <div className="section-content">
                    <div>
                      {amountDueInput}
                      {currenciesInput}
                    </div>
                    <div>{descriptionInput}</div>
                    <div>{invoiceInput}</div>
                  </div>
                ) : null}
              </div>
              <div>
                <input
                  onChange={event => store.setAction(event.target.value)}
                  id="chargeInput"
                  name="ransomOrCharge"
                  value="CHARGE"
                  type="radio"
                  checked={store.action === "CHARGE"}
                  disabled={!store.isAbleToChargeCardOnFile || store.isStripeDown}
                />
                <label
                  className={cx({
                    "ransom-option": true,
                    disabled: !store.isAbleToChargeCardOnFile || store.isStripeDown,
                  })}
                  htmlFor="chargeInput"
                >
                  Charge card on file
                </label>
                {store.action === "CHARGE" && store.customerHasCreditCards && store.hasStripe ? (
                  <div className="section-content">
                    <div>{creditCardsInput}</div>
                    <div>
                      {amountDueInput}
                      {currenciesInput}
                    </div>
                    <div>{descriptionInput}</div>
                    <div>{invoiceInput}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  getAlert() {
    const { store } = this.props;

    if (store.showSaveDefaultPaymentCollectionPrompt) {
      const onCancel = () => store.submitRansom();

      return (
        <Modal size="small" open onClose={() => onCancel()}>
          <Modal.Header>Default Payment Method</Modal.Header>
          <Modal.Content>
            <div>
              <p>Would you like to make this your customer&rsquo;s default payment collection method?</p>
              <div className="do-not-show-save-default-payment-collection-prompt">
                <input
                  id="doNotShowSaveDefaultPaymentCollectionPrompt"
                  type="checkbox"
                  value={store.doNotShowSavePaymentCollectionMethodPrompt}
                  onChange={() => store.toggleDoNotShowSavePaymentCollectionMethodPrompt()}
                />
                &nbsp;
                <label htmlFor="doNotShowSaveDefaultPaymentCollectionPrompt" className="help">
                  Don&rsquo;t show this message again
                </label>
              </div>
            </div>
          </Modal.Content>
          <Modal.Actions>
            <PromiseButton
              positive
              icon="checkmark"
              labelPosition="right"
              content="Set as Default"
              onClick={() => {
                store.setSavePaymentCollectionMethod(true);
                return store.submitRansom();
              }}
            />
            <span>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <a
                href="#"
                onClick={event => {
                  event.preventDefault();
                  onCancel();
                }}
              >
                Skip
              </a>
              &nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </Modal.Actions>
        </Modal>
      );
    }

    if (store.showRansomRemovalWarning) {
      // on cancel close this modal
      const onCancel = () => store.setShowRansomRemovalWarning(false);

      return (
        <Modal size="small" open onClose={() => onCancel()}>
          <Modal.Header>Release Tour</Modal.Header>
          <Modal.Content>
            By selecting this option the requirement for customer payment will be canceled and the tour will be
            published. Do you want to continue?
          </Modal.Content>
          <Modal.Actions>
            <PromiseButton
              positive
              icon="checkmark"
              labelPosition="right"
              content="Release"
              onClick={() => store.submitRansom()}
            />
            <span>
              &nbsp;&nbsp;&nbsp;&nbsp;
              <a
                href="#"
                onClick={event => {
                  event.preventDefault();
                  onCancel();
                }}
              >
                Cancel
              </a>
              &nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </Modal.Actions>
        </Modal>
      );
    }

    return null;
  }

  render() {
    const { store, modalOpen, openModalCallback, closeModalCallback } = this.props;
    // Prevent closing the modal while saving.
    const savingAwareCloseModalCallback = () => {
      if (store.saving) {
        return;
      }

      closeModalCallback();
    };

    return (
      <div>
        {this.getAlert()}
        <button
          type="button"
          style={{
            fontSize: "1em",
            textTransform: "none",
            minWidth: "140px",
          }}
          onClick={() => openModalCallback()}
          className="button default"
        >
          Delivery Manager
        </button>
        <Modal open={modalOpen} closeIcon="close" onClose={() => savingAwareCloseModalCallback()}>
          <Modal.Header>
            <strong>Delivery Manager</strong>
            {this.getSubHeader()}
          </Modal.Header>
          <Modal.Content>
            <div className="ui two column very relaxed grid">
              <div
                className="column"
                style={{
                  position: "relative",
                }}
              >
                <Form>
                  {store.canPublishTour ? this.getProducts() : this.getHandoffNotes()}
                  {this.getPaymentCollectionSection()}
                </Form>
                <div className="vertical-divider" />
              </div>
              <Sidebar auditLogs={store.auditLogs} tourNotes={store.tourNotes} customerNotes={store.customerNotes} />
            </div>
          </Modal.Content>
          <Modal.Actions>
            {store.skipToEmailNotification ? (
              <a className="ui button default" href={store.skipToEmailNotification}>
                Skip To Email Notification
              </a>
            ) : null}
            <PromiseButton
              className="default"
              onClick={() => {
                if (store.shouldShowSaveDefaultPaymentCollectionPrompt()) {
                  store.setShowSaveDefaultPaymentCollectionPrompt(true);
                  // consistent return.
                  return Promise.resolve();
                }

                if (store.actionWillCancelRansom) {
                  store.setShowRansomRemovalWarning(true);
                  // consistent return.
                  return Promise.resolve();
                }

                return store.submitRansom();
              }}
            >
              {store.deliverButtonLabel}
            </PromiseButton>
            &nbsp; or &nbsp;
            <a
              onClick={e => {
                e.preventDefault();
                savingAwareCloseModalCallback();
              }}
              href="#"
            >
              cancel
            </a>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

Ransom.propTypes = {
  store: PropTypes.instanceOf(TourDeliver),
  modalOpen: PropTypes.bool,
  openModalCallback: PropTypes.func.isRequired,
  closeModalCallback: PropTypes.func.isRequired,
};

export default Ransom;
